import React from 'react';

import {
  errorFieldsTypes,
  ErrorsFields,
  FormState,
  MultiOptionValue,
  prefixTypes,
  TakeActionFormState,
} from '@/core/interfaces/common';

import { handleNumberFormat } from '@/utils/helpers';

import { ContactFields } from '../ContactFields';

export interface CustomFieldErrors {
  value: Array<string>;
}

export interface ContactFieldsContainerProps {
  backgroundColor: string;
  error: ErrorsFields;
  formState: FormState | TakeActionFormState;
  handleBlurAddress?: (streetAddress?: string, zipCode?: string) => void;
  setError: React.Dispatch<React.SetStateAction<ErrorsFields>>;
  setFormState: React.Dispatch<React.SetStateAction<FormState | TakeActionFormState>>;
  textColor: string;
  CustomContactFieldsComponent?: React.ComponentType<any>;
}

export const ContactFieldsContainer = ({
  backgroundColor,
  error,
  formState,
  handleBlurAddress,
  setError,
  setFormState,
  textColor,
  CustomContactFieldsComponent,
}: ContactFieldsContainerProps) => {
  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;

    setFormState(prevValues => ({
      ...prevValues,
      prefix: value as prefixTypes,
    }));
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      target: {
        checked,
        name,
        type,
        value,
      },
    } = event;

    const inputValue = type === 'checkbox' ? checked : value;

    setFormState(prevValues => ({
      ...prevValues,
      [name]: inputValue,
    }));
  };

  const handleCustomFieldChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    index: number
  ) => {
    const customFieldsData = [...formState.customFields];

    customFieldsData[index] = {
      ...customFieldsData[index],
      value: event.target.value,
    };

    setFormState(prevValues => ({
      ...prevValues,
      customFields: customFieldsData,
    }));
  };

  const handleMultiCustomFieldChange = (
    values: Array<MultiOptionValue>,
    index: number
  ) => {
    const customFieldsData = [...formState.customFields];

    customFieldsData[index] = {
      ...customFieldsData[index],
      values,
    };

    setFormState(prevValues => ({
      ...prevValues,
      customFields: customFieldsData,
    }));
  };

  const handlePhoneInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    setFormState(prevValues => ({
      ...prevValues,
      phoneNumber: handleNumberFormat(value) || '',
    }));
  };

  const handleChangeAddress = (value: string) => {
    setFormState(prevValues => ({
      ...prevValues,
      streetAddress: value,
    }));
  };

  const handleSetZipCode = (value: string) => setFormState(prevValues => ({
    ...prevValues,
    zipCode: value,
  }));

  const handleClearFieldError = (fieldName: string) => {
    setError(prevValues => ({
      ...prevValues,
      [fieldName]: [],
    }));
  };

  const handleClearCustomFieldError = (index: number) => {
    if (error?.customFields) {
      const customFieldsData = [...error.customFields];

      customFieldsData[index] = {
        value: [],
      };

      setError(prevValues => ({
        ...prevValues,
        customFields: customFieldsData,
      }));
    }
  };

  const renderContactError = (errorField: errorFieldsTypes): string => {
    if (error && !!error[errorField] && !!error[errorField]?.length) {
      const errorArray = error[errorField];

      if (errorArray) {
        return errorArray[0];
      }
    }

    return '';
  };

  const contactFieldsProps = {
    backgroundColor,
    customFields: formState.customFields,
    email: formState.email,
    error,
    firstName: formState.firstName,
    handleBlurAddress,
    handleChangeAddress,
    handleClearCustomFieldError,
    handleClearFieldError,
    handleCustomFieldChange,
    handleInputChange,
    handleMultiCustomFieldChange,
    handlePhoneInputChange,
    handleSelectChange,
    handleSetZipCode,
    lastName: formState.lastName,
    phoneNumber: formState.phoneNumber,
    prefix: formState.prefix,
    renderContactError,
    streetAddress: formState.streetAddress,
    zipCode: formState.zipCode,
  };

  return (
    <>
      {CustomContactFieldsComponent ? (
        <CustomContactFieldsComponent {...contactFieldsProps} />
      ) : (
        <ContactFields {...contactFieldsProps} />
      )}
    </>
  );
};

ContactFieldsContainer.defaultProps = {
  handleBlurAddress: () => null,
};

